import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  useESGDataEntryStore,
  useESGCollaborationStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { Trash2, UserCheck } from "lucide-react";
import { useState } from "react";
import DisclosureEntryFields from "./DisclosureEntryFields";
import EvidenceUpload from "./EvidenceUpload";
import DelegateEntryDialog from "./DelegateEntryDialog";

const DataEntry = observer(({ matchingDisclosures, disclosure }) => {
  const { t } = useTran();
  const { currentEntries, errors, addEntry, removeEntry, updateEntry } =
    useESGDataEntryStore();
  const {
    isDisclosureDelegated,
    getDisclosureDelegation,
    isEntryDelegated,
    getEntryDelegation,
    removeEntryDelegation,
    loading,
  } = useESGCollaborationStore();

  const { code, title } = disclosure;
  const disclosureDelegated = isDisclosureDelegated(code);
  const disclosureDelegation = getDisclosureDelegation(code);
  const [delegateDialog, setDelegateDialog] = useState({
    open: false,
    entryId: null,
    entryLabel: "",
  });

  let inputType, unitOptions, subEntryDefs;

  if (matchingDisclosures.length > 1) {
    inputType = "subEntries";
    unitOptions = [];

    subEntryDefs = [...matchingDisclosures]
      .sort((a, b) => (a.subEntryIndex ?? 0) - (b.subEntryIndex ?? 0))
      .map((d) => ({
        inputType: d.inputType,
        unitOptions: d.unitOptions || [],
        title: d.subEntryTitle || d.title,
      }));
  } else {
    inputType = disclosure.inputType;
    unitOptions = disclosure.unitOptions || [];
    subEntryDefs = [];
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-radius bg-card border-border border">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex gap-x-2">
          <p className="text-sm font-semibold">{t(code)}</p>
          <p className="text-sm text-muted-foreground">{t(title)}</p>
        </div>
        <Button
          size="sm"
          onClick={() => addEntry(code)}
          disabled={disclosureDelegated}
        >
          {t("Add New Entry")}
        </Button>
      </div>

      <div className="flex-1 min-h-0 space-y-3 overflow-y-auto p-3">
        {currentEntries.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {t("No entries yet")}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => addEntry(code)}
              disabled={disclosureDelegated}
            >
              {t("Add Entry")}
            </Button>
          </div>
        ) : (
          currentEntries.map((entry, index) => {
            const entryDelegated = isEntryDelegated(code, entry.entryId);
            const entryDelegation = getEntryDelegation(code, entry.entryId);
            const isDelegated = disclosureDelegated || entryDelegated;
            // Disclosure-level email, or entry-level email
            const delegationEmail =
              entryDelegation?.email || disclosureDelegation?.email;

            return (
              <div
                key={index}
                className={`rounded-md border p-3 ${isDelegated ? "border-amber-500/50 dark:border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/20" : "border-border"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Input
                    value={entry.label || ""}
                    placeholder={`${t("Entry")} ${index + 1}`}
                    onChange={(e) =>
                      updateEntry(code, index, "label", e.target.value)
                    }
                    className="h-7 w-36 text-xs font-semibold"
                    disabled={isDelegated}
                  />
                  {currentEntries.length > 1 && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      onClick={() => removeEntry(code, index)}
                      disabled={isDelegated}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {isDelegated && (
                  <div className="mb-3 p-2 bg-amber-100/80 dark:bg-amber-950/40 border border-amber-400/50 dark:border-amber-500/30 rounded-md">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <UserCheck
                          size={14}
                          className="text-amber-700 dark:text-amber-400"
                        />
                        <div>
                          <p className="text-xs font-medium text-amber-900 dark:text-amber-300">
                            {t("Delegated to")}
                          </p>
                          <Badge
                            variant="outline"
                            className="text-xs bg-background border-amber-400/50 dark:border-amber-500/30"
                          >
                            {delegationEmail}
                          </Badge>
                        </div>
                      </div>
                      {/* Change/Remove only for entry-level delegation.
                          Disclosure-level is managed in Step 2. */}
                      {entryDelegated && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() =>
                              setDelegateDialog({
                                open: true,
                                entryId: entry.entryId,
                                entryLabel: entry.label,
                              })
                            }
                            disabled={loading}
                          >
                            {t("Change")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-destructive"
                            onClick={() =>
                              removeEntryDelegation(code, entry.entryId)
                            }
                            disabled={loading}
                          >
                            {t("Remove")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Delegate Entry button: hidden if disclosure or entry is delegated */}
                {!isDelegated && (
                  <div className="mb-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() =>
                        setDelegateDialog({
                          open: true,
                          entryId: entry.entryId,
                          entryLabel: entry.label,
                        })
                      }
                      disabled={loading}
                    >
                      <UserCheck size={14} />
                      {t("Delegate Entry")}
                    </Button>
                  </div>
                )}

                <DisclosureEntryFields
                  inputType={inputType}
                  unitOptions={unitOptions}
                  subEntryDefs={subEntryDefs}
                  entry={entry}
                  errors={errors.get(`${code}_${index}`)}
                  onChange={(field, value) =>
                    updateEntry(code, index, field, value)
                  }
                  disabled={isDelegated}
                />
                <EvidenceUpload
                  entry={entry}
                  code={code}
                  entryIndex={index}
                  disabled={isDelegated}
                />
              </div>
            );
          })
        )}
      </div>

      <DelegateEntryDialog
        open={delegateDialog.open}
        onOpenChange={(open) =>
          setDelegateDialog({ open, entryId: null, entryLabel: "" })
        }
        code={code}
        entryId={delegateDialog.entryId}
        entryLabel={delegateDialog.entryLabel}
      />
    </div>
  );
});

export default DataEntry;
